import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import JsonLd from "@/components/base/JsonLd";
import { getArticleSchema, getOrganizationSchema, getBreadcrumbSchema } from "@/components/base/SchemaOrg";
import { articles, hotContents, latestContents } from "@/mocks/home";
import type { BreadcrumbItem } from "@/components/base/SchemaOrg";

const allArticles = [...articles];
const allRecommendations = [...hotContents, ...latestContents].slice(0, 4);
const siteUrl = "https://www.91madou.com";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-4">抱歉，您访问的文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categorySlug =
    article.category === "电影" ? "movie" : article.category === "电视剧" ? "tv" : article.category === "综艺" ? "variety" : "anime";

  const breadcrumbs: BreadcrumbItem[] = [
    { name: "首页", url: siteUrl },
    { name: article.category, url: `${siteUrl}/category/${categorySlug}` },
    { name: article.title, url: `${siteUrl}/article/${article.slug}` },
  ];

  return (
    <>
      <JsonLd data={getOrganizationSchema()} />
      <JsonLd data={getBreadcrumbSchema(breadcrumbs)} />
      <JsonLd data={getArticleSchema(article.slug)} />
      <ScrollToTop />
      <Navbar />
      <main>
        <article>
          <header className="pt-20 pb-8 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-4xl mx-auto">
                <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4 flex-wrap" aria-label="面包屑导航">
                  <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">首页</Link>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                  <Link to={`/category/${categorySlug}`} className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">{article.category}</Link>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                  <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
                </nav>
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3">
                  {article.category}
                </span>
                <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                </div>
              </div>
            </div>
          </header>

          <section className="w-full px-4 md:px-6 py-8">
            <div className="max-w-4xl mx-auto">
              <div className="rounded-xl overflow-hidden mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={article.title}
                  className="w-full aspect-video object-cover object-top"
                />
              </div>

              <div className="prose prose-lg max-w-none">
                <p className="text-foreground-700 leading-relaxed text-base mb-6">
                  {article.summary}
                </p>
                <div className="text-foreground-700 leading-relaxed text-base space-y-4">
                  {article.content.split("。").filter(Boolean).map((sentence, i) => (
                    <p key={i}>{sentence}。</p>
                  ))}
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-foreground-500">标签：</span>
                  <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs whitespace-nowrap">
                    {article.category}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs whitespace-nowrap">
                    91麻豆免费视频网站
                  </span>
                  <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs whitespace-nowrap">
                    影视网站免费观看
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {allRecommendations.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.slug}`}
                    className="group block rounded-xl overflow-hidden bg-background-50 transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        title={item.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
                          {item.rating}
                        </span>
                      </div>
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="font-heading font-semibold text-sm text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors duration-200">
                        {item.title}
                      </h3>
                      <span className="text-xs text-foreground-500">{item.category}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}